import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

/**
 * Playback control for a wall of video.
 *
 * A wall of looping video is by a wide margin the largest motion this product
 * ever puts on a screen, so it obeys the house motion floor exactly the way a
 * transition does: when the operating system asks for reduced motion, nothing
 * plays and every plate holds its still. That is a floor, not a default, so
 * there is deliberately no in-page control that can start playback against it.
 *
 * Independently of the system setting the viewer can hold the wall still from
 * the filter bar, because wanting fifteen videos to stop moving is a reasonable
 * thing to want without changing a system preference to get it.
 *
 * THE DETAIL STAGE IS THE ONE EXCEPTION (2026-09-28). It plays one video the
 * viewer opened on purpose and carries its own Pause (WCAG 2.2.2), passed to the
 * plate as `viewerPaused`. Under reduced motion that Pause starts pressed, so
 * nothing moves on its own; the stage plays only when the viewer presses it.
 * No wall plate has one, so the floor above still holds every wall.
 *
 * SOUND IS ONE VOICE AT A TIME (2026-09-25, supersedes "a wall is muted
 * always"). Plates autoplay muted, and a plate is heard only after the viewer
 * clicks its sound chip: that click is the gesture a browser asks for before it
 * lets a page make a noise. At most one plate is heard, so choosing a plate
 * mutes every other one. `soundPlateId` names it, and null is a silent wall.
 *
 * A detail stage keeps its own toggle, because there is exactly one video
 * playing there and the viewer chose to open it. That choice lives here rather
 * than in the stage so it survives moving between items and reopening the
 * panel. The stage and a wall plate are still two voices, so turning either one
 * on turns the other off, and the latest choice wins.
 *
 * Everything starts OFF: sound that starts itself is the thing everybody hates.
 * The viewer's pause stops sound as well as motion, and so does the
 * reduced-motion floor, because a plate that is held still is not being heard.
 *
 * AN OPEN STAGE HOLDS THE WALL (2026-09-26). While a detail panel is open over
 * the wall, every wall plate is paused, the one with sound included, and the
 * sound plate is cleared: the viewer is looking at the stage now, and the stage
 * keeps its own sound toggle. The page says when a stage is open.
 *
 * One provider per page, wrapping the wall AND anything that opens out of it, so
 * one switch and one reduced-motion floor govern every video on screen.
 */

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

interface PlaybackValue {
  /** Whether a plate is allowed to play at all right now. */
  allowed: boolean;
  /** The system asked for reduced motion, which holds playback off at the floor. */
  reduced: boolean;
  /** The viewer stopped the wall themselves. */
  paused: boolean;
  /** Pausing also clears the wall's sound plate: a held wall is a silent one. */
  togglePaused: () => void;
  /** The viewer turned sound on for a detail stage. */
  soundOn: boolean;
  /** Turning the stage on also clears the wall's sound plate: one voice. */
  toggleSound: () => void;
  /** The one wall plate the viewer asked to hear, or null for a silent wall. */
  soundPlateId: string | null;
  /** Name the plate to hear, which mutes every other plate and the stage, or
   *  pass null to silence the wall. */
  setSoundPlate: (id: string | null) => void;
  /** A detail stage is open over the wall, so no wall plate plays or sounds. */
  stageOpen: boolean;
}

/** Default is held-still and silent: a plate rendered outside a provider stays a
 *  still rather than assuming it may play, and never assumes it may be heard. */
const PlaybackContext = createContext<PlaybackValue>({
  allowed: false,
  reduced: false,
  paused: true,
  togglePaused: () => {},
  soundOn: false,
  toggleSound: () => {},
  soundPlateId: null,
  setSoundPlate: () => {},
  stageOpen: false,
});

export function PlaybackProvider({
  children,
  stageOpen = false,
}: {
  children: ReactNode;
  /** A detail stage is open over the wall. Opening one clears the sound plate. */
  stageOpen?: boolean;
}) {
  const reduced = useMediaQuery(REDUCED_MOTION_QUERY);
  const [paused, setPaused] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [soundPlateId, setSoundPlateId] = useState<string | null>(null);

  /* Adjusted while rendering, the pattern React documents for resetting state
     when an input changes: an effect would leave the wall plate audible for a
     frame after the stage opened. The plates also gate on `stageOpen` directly,
     so the sound stops on the same render either way. */
  const [stageWas, setStageWas] = useState(stageOpen);
  if (stageOpen !== stageWas) {
    setStageWas(stageOpen);
    if (stageOpen) setSoundPlateId(null);
  }

  const value = useMemo<PlaybackValue>(
    () => ({
      allowed: !reduced && !paused,
      reduced,
      paused,
      togglePaused: () => {
        if (!paused) setSoundPlateId(null);
        setPaused((p) => !p);
      },
      soundOn,
      toggleSound: () => {
        if (!soundOn) setSoundPlateId(null);
        setSoundOn((s) => !s);
      },
      soundPlateId,
      setSoundPlate: (id) => {
        if (id !== null) setSoundOn(false);
        setSoundPlateId(id);
      },
      stageOpen,
    }),
    [reduced, paused, soundOn, soundPlateId, stageOpen],
  );

  return <PlaybackContext.Provider value={value}>{children}</PlaybackContext.Provider>;
}

export function usePlayback(): PlaybackValue {
  return useContext(PlaybackContext);
}
